import { useState, useEffect } from "react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

// Cache with 5-minute TTL so toggle changes propagate within a reasonable time
let _cache = null;
let _cacheAt = 0;
let _promise = null;
const CACHE_TTL = 5 * 60 * 1000;

function fmtPrice(value, interval) {
  if (!value) return "R$0";
  // Centavos aparecem quando existem: arredondá-los para o real cheio anunciaria
  // um preço que não é o cobrado.
  const temCentavos = Math.abs(value - Math.round(value)) > 0.001;
  return `R$${Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: temCentavos ? 2 : 0,
    maximumFractionDigits: temCentavos ? 2 : 0,
  })}`;
}

function isCacheValid() {
  return _cache && (Date.now() - _cacheAt) < CACHE_TTL;
}

export function usePlans() {
  const [plans, setPlans] = useState(isCacheValid() ? _cache : null);
  const [loading, setLoading] = useState(!isCacheValid());

  useEffect(() => {
    if (isCacheValid()) { setPlans(_cache); setLoading(false); return; }
    if (!_promise) {
      _promise = fetch(`${API}/plans`).then(r => r.json()).catch(() => null);
    }
    _promise.then(data => {
      if (data?.plans) {
        _cache = data.plans;
        _cacheAt = Date.now();
        _promise = null;
        setPlans(_cache);
      }
      setLoading(false);
    });
  }, []);

  // Returns plan by key, or null
  const getByKey = (key) => plans?.find(p => p.key === key) || null;

  // Landing Pricing shape: { free, pro }. A chave "free" é o plano de entrada
  // exibido na landing — hoje o START pago. O FREE antigo saiu de venda e
  // continua valendo só para quem já o tinha, então não aparece aqui.
  // Returns null only while loading (to allow fallback during initial load)
  const landingPricing = () => {
    if (loading && !plans) return null;
    const entrada = getByKey("start") || getByKey("free");
    const pro = getByKey("pro_monthly");
    if (!pro) return null;
    return {
      free: entrada && entrada.active !== false ? {
        name: entrada.label || "START",
        price: fmtPrice(entrada.price),
        period: "/mês",
        features: entrada.features || [],
        cta: entrada.price > 0 ? `Assinar ${entrada.label || "START"}` : "Começar grátis",
        microcopy: entrada.price > 0 ? "cancele quando quiser" : "sem cartão de crédito",
      } : null,
      pro: {
        name: "PRO",
        price: fmtPrice(pro.price),
        oldPrice: pro.old_price ? fmtPrice(pro.old_price) : null,
        period: "/mês",
        annualNote: (() => {
          const ann = plans?.find(p => p.key === "pro_annual");
          return ann ? `ou ${fmtPrice(ann.price)}/ano — economize 2 meses` : "";
        })(),
        features: pro.features || [],
        cta: "Criar conta e assinar",
        microcopy: "cancele quando quiser",
      },
    };
  };

  // PlanoPage shape: object with only active PRO plans
  // Returns null while loading (to allow fallback), {} if no pro plans active
  // Planos que o escritório pode contratar a partir do que ele tem hoje.
  //
  // Antes isto era fixo em pro_monthly e pro_annual, de quando só existiam
  // FREE e PRO. Com o START no meio, quem estava no FREE nunca via a oferta de
  // o plano de entrada — o degrau mais fácil de subir ficava escondido.
  //
  // Agora sai da lista real de planos: o que o admin ativar aparece sozinho, e
  // o plano atual e os de graça ficam de fora (não se "contrata" o que já se
  // tem, nem o que não é vendido).
  const ORDEM = ["start", "pro_monthly", "pro_annual"];

  const planPagePlans = (planoAtual = null) => {
    if (loading && !plans) return null;

    const chaveDoPlanoAtual = {
      start: "start",
      pro: null,  // o PRO não tem upgrade: a tela mostra a gestão da assinatura
    }[planoAtual] ?? null;

    const vendaveis = (plans || []).filter(
      (p) => p.active !== false && Number(p.price) > 0 && p.key !== chaveDoPlanoAtual,
    );
    if (vendaveis.length === 0) return null;

    const anual = (p) => p.interval === "year" || p.key === "pro_annual";
    const result = {};
    for (const p of vendaveis.sort(
      (a, b) => (ORDEM.indexOf(a.key) + 1 || 99) - (ORDEM.indexOf(b.key) + 1 || 99),
    )) {
      result[p.key] = {
        label: p.label || p.key,
        price: fmtPrice(p.price),
        oldPrice: p.old_price ? fmtPrice(p.old_price) : null,
        period: anual(p) ? "/ano" : "/mês",
        highlight: p.old_price
          ? (anual(p) ? "Oferta de lançamento · 2 meses grátis" : "Oferta de lançamento")
          : null,
        features: p.features || [],
      };
    }
    return result;
  };

  // Preço do plano de entrada, do jeito que se escreve numa frase.
  //
  // Existe porque o valor estava cravado em quatro telas. Quando o START
  // mudou de preço no admin, o banco mudou e as telas
  // continuaram anunciando o preço antigo — inclusive na tela de login, que é
  // onde a promessa é feita.
  const precoDeEntrada = () => {
    const entrada = getByKey("start") || getByKey("free");
    if (!entrada || !entrada.price) return null;
    return fmtPrice(entrada.price);
  };

  return { plans, loading, getByKey, landingPricing, planPagePlans, precoDeEntrada };
}
